function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import React from 'react';
import compact from 'lodash/compact';
import { useHistory } from 'react-router-dom';
import { useGlobalContext } from 'seagull';
import { FullPageModalAdditionalActions } from 'seagull';
import { AgreementsListLine, MoneyChange, MoneyReceive, Void } from 'seagull/icons';
import { usePaidExternally } from "../../hooks/usePaidExternally";
import { useResendInvoice } from "../../hooks/useResendInvoice";
import { useStore, useStoreActions } from "../../contexts";
import { useCopyDirectLink } from "../../hooks/useCopyDirectLink";
import { selectors } from "../../store/state-selectors";
import { useDownloadInvoicePdf } from "../../hooks/invoice/useDownloadInvoicePdf";
import { Intent } from "../../infra/access-control";
import { DownloadDeliverablesModal } from "./DownloadDeliverablesModal";
import { getServiceAndActiveBillCommands, makeStopPaymentInvoiceAction, makeResumePaymentInvoiceAction, terminatedTooltip } from "./FullPageInvoice.utils";
import { invoiceModel, relationshipModel } from "../../models";
import { routes } from "../../constants";
import { allowResendAction, allowVoidAction } from "../../business-logic/invoiceLogic";
export default function FullPageInvoiceAdditionalActions(props) {
  const {
    invoice,
    relationship,
    serviceAndBillCommands,
    isVendor,
    reloadInvoice,
    onStopPayment,
    onResumePayment,
    onVoidInvoice,
    onRequestRefund
  } = props;
  const {
    shouldShowMarkAsPaid,
    markAsPaid
  } = usePaidExternally();
  const {
    isResendingInvoice,
    resendInvoice
  } = useResendInvoice();
  const {
    isFetchingLink,
    copyDirectLink
  } = useCopyDirectLink();
  const {
    logBI
  } = useStoreActions();
  const globalContext = useGlobalContext();
  const isMemberUser = useStore(selectors.user.isMemberUser);
  const isClientMember = !isVendor && isMemberUser;
  const {
    isDownloadingInvoicePdf,
    downloadInvoicePdf
  } = useDownloadInvoicePdf();
  const serviceAndActiveBillCommands = getServiceAndActiveBillCommands(serviceAndBillCommands);
  const history = useHistory();
  const isTerminated = relationshipModel.isTerminated(relationship);
  const inlineActions = compact([{
    text: 'Download PDF',
    testid: 'fpi-download-pdf',
    intent: Intent.InvoiceDownload,
    disabled: isDownloadingInvoicePdf || isClientMember || !invoice.pdfExists,
    tooltipTitle: invoice.pdfExists ? undefined : 'Generating PDF',
    onClick: () => downloadInvoicePdf(invoice.id)
  }, allowResendAction(invoice.status, isVendor) && {
    text: 'Resend invoice',
    testid: 'fpi-resend-invoice',
    intent: Intent.InvoiceResend,
    disabled: isResendingInvoice || isClientMember,
    onClick: () => resendInvoice(invoice).then(() => reloadInvoice())
  }, {
    text: 'Copy link',
    testid: 'fpi-copy-link',
    intent: Intent.InvoiceCopyLink,
    disabled: isFetchingLink || isClientMember,
    onClick: () => {
      logBI(14001);
      copyDirectLink(relationship.id, {
        type: 'invoice',
        id: invoice.id
      });
    }
  }, serviceAndActiveBillCommands.length > 0 && {
    text: 'Download files',
    testid: 'fpi-download-files',
    disabled: isClientMember,
    onClick: () => {
      globalContext.showModal({
        content: _ref => {
          let {
            dismissModal
          } = _ref;
          return /*#__PURE__*/React.createElement(DownloadDeliverablesModal, {
            agreement: relationship,
            serviceAndBillCommands: serviceAndActiveBillCommands,
            dismissModal: dismissModal
          });
        }
      });
    }
  }]);
  const dropdownActions = compact([{
    text: 'View agreement',
    testid: 'fpi-view-agreement',
    disabled: isClientMember,
    iconStart: /*#__PURE__*/React.createElement(AgreementsListLine, null),
    onClick: () => history.push(routes.agreement(relationshipModel.id(relationship)))
  }, shouldShowMarkAsPaid(invoice.status, isVendor) && {
    text: 'Mark as paid externally',
    testid: 'fpi-mark-paid-externally',
    intent: Intent.InvoiceMarkPaidExternally,
    disabled: isClientMember,
    iconStart: /*#__PURE__*/React.createElement(MoneyReceive, null),
    onClick: () => markAsPaid(invoice, () => reloadInvoice())
  }, isVendor && (invoiceModel.isPaid(invoice) || invoiceModel.isConfirmed(invoice) || invoiceModel.isProcessing(invoice)) && {
    text: 'Request a refund',
    testid: 'fpi-request-refund',
    intent: Intent.RequestRefund,
    iconStart: /*#__PURE__*/React.createElement(MoneyChange, null),
    onClick: onRequestRefund
  }, allowVoidAction(invoice.status, isVendor) && {
    text: 'Void invoice',
    testid: 'fpi-void-invoice',
    intent: Intent.InvoiceVoid,
    disabled: isClientMember || isTerminated,
    tooltipTitle: isTerminated ? terminatedTooltip : undefined,
    skin: 'error',
    iconStart: /*#__PURE__*/React.createElement(Void, null),
    onClick: onVoidInvoice
  }, !isVendor && invoiceModel.isPending(invoice) && _objectSpread(_objectSpread({}, makeStopPaymentInvoiceAction(isTerminated)), {}, {
    testid: 'fpi-stop-payment',
    skin: 'error',
    iconStart: /*#__PURE__*/React.createElement(Void, null),
    onClick: onStopPayment
  }), !isVendor && invoiceModel.isPaymentStopped(invoice) && _objectSpread(_objectSpread({}, makeResumePaymentInvoiceAction(isTerminated)), {}, {
    testid: 'fpi-resume-payment',
    iconStart: /*#__PURE__*/React.createElement(MoneyReceive, null),
    onClick: onResumePayment
  })]);
  return /*#__PURE__*/React.createElement(FullPageModalAdditionalActions, {
    inlineActions: inlineActions,
    dropdownActions: dropdownActions
  });
}